import { useState } from "react";

import { LIBRARY_KEYS } from "@corca/data/libraries";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { FormButton } from "@/components/form/FormButton";
import { FormToggle } from "@/components/form/FormToggle";
import { StatelessActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { twMerge } from "@/utils/twMerge";

import type { DocumentModel } from "@/models/DocumentModel";
import type { ChangeEvent } from "react";

export const LibrariesPanel = observer(function LibrariesPanel({ doc }: { doc: DocumentModel }) {
  const t = useTranslations("sidebar.libraries");
  const { libraries } = doc;
  const [isOpen, setIsOpen] = useState(false);
  const enabledCount = LIBRARY_KEYS.filter((key) => libraries.isKeyEnabled(key)).length;
  const disabled = !libraries.enabled || libraries.status !== "ready";

  const handleToggle = () => {
    libraries.toggle();
    if (libraries.enabled) setIsOpen(true);
  };

  return (
    <StatelessActionsDisclosure
      data-panel="libraries"
      title={
        <>
          {t("title")}
          <FormToggle
            aria-label={t("enable")}
            checked={libraries.enabled}
            className="ml-auto"
            onClick={(e) => e.stopPropagation()}
            onChange={handleToggle}
            asInput
          />
        </>
      }
      isOpen={isOpen}
      onTriggerClick={() => setIsOpen((open) => !open)}
    >
      <div className="flex flex-col gap-0.5 text-sm px-2 pb-1">
        {libraries.status === "failed" && (
          <div className="flex items-center gap-2 text-xs text-red-500 pb-1">
            <span className="flex-1">{t("loadFailed")}</span>
            <FormButton variant="accent" size="sm" onClick={() => libraries.retry()}>
              {t("retry")}
            </FormButton>
          </div>
        )}
        <Checkbox
          label={t("core")}
          checked={enabledCount === LIBRARY_KEYS.length}
          indeterminate={enabledCount > 0 && enabledCount < LIBRARY_KEYS.length}
          disabled={disabled}
          onChange={(e) => libraries.setAllKeys(e.target.checked)}
          className="flex items-center gap-2 font-medium"
        />
        {LIBRARY_KEYS.map((key) => (
          <Checkbox
            key={key}
            label={key}
            checked={libraries.isKeyEnabled(key)}
            disabled={disabled}
            onChange={(e) => libraries.setKeyEnabled(key, e.target.checked)}
            className="flex items-center gap-2 pl-5"
          />
        ))}
      </div>
    </StatelessActionsDisclosure>
  );
});

function Checkbox({
  label,
  checked,
  indeterminate = false,
  disabled,
  onChange,
  className,
}: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  disabled: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}) {
  return (
    <label className={twMerge(className, disabled && "opacity-50")}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        ref={(input) => {
          if (input) input.indeterminate = indeterminate;
        }}
      />
      <span className="truncate">{label}</span>
    </label>
  );
}
